<template>
    <hts-map-travel
        ref="map"
        :route="route"
        :route-draw="routeDraw"
        :route-actual="routeActual"
        :route-estimate="routeEstimate"
        :lat-lng-location-routes="latLngLocationRoutes"
        :show-location="showLocation"
        center-marker
        class="max-h-[500px]" />
</template>
<script setup lang="ts">
const { $location } = useNuxtApp()

const props = defineProps({
  journey: Object,
  showLocation: {
    type: Boolean,
    default: true
  }
})

const { journey, showLocation } = toRefs(props)

const route = ref<any>(null)
const routeDraw = ref<{ [key: string]: any | any[] }>({})
const routeActual = ref<{ [key: string]: any | any[] }>({})
const routeEstimate = ref<{ [key: string]: any | any[] }>({})
const latLngLocationRoutes = ref([] as { [key: string]: any | any[] }[])

const CPositions = computed(() => journey?.value?.journey || [])
const CRoutingPositions = computed(() => journey?.value?.routingPositions || {})
const CJourneyPositioning = computed(() => journey?.value?.journeyPositioning || {})

onMounted(() => {
  nextTick(() => {
    setDataDrawRoute()
    setDataDrawRouteActual()
    setDataDrawRouteEstimate()
    setDataCreateMarker()
  })
})

// tách nhỏ tuyến để vẻ A -> B -> C  ==>  A -> B, B -> C
const setDataDrawRoute = () => {
  const r = [] as any[]

  const positions = CPositions.value

  let { startAtLocation, stopAtLocation } = journey?.value || {}
  if (Array.isArray(startAtLocation)) {
    startAtLocation = startAtLocation[0] || null
  }
  if (Array.isArray(stopAtLocation)) {
    stopAtLocation = stopAtLocation[0] || null
  }

  // journey?.value?.processStatus !== 0
  if (startAtLocation) {
    const { locationName } = startAtLocation
    const { lat, lng } = startAtLocation

    r.push({
      originData: {
        lat: parseFloat(lat),
        lng: parseFloat(lng)
      },
      destinationData: {
        lat: parseFloat(positions[0].lat),
        lng: parseFloat(positions[0].lng)
      },
      data: {
        startLocation: startAtLocation._id,
        endLocation: positions[0]._id,
        nodeLocationName: locationName
      }
    })
  }

  for (let i = 0; i < positions?.length - 1; i++) {
    const overViewPolyLine = getOverViewPolyLine(positions[i]._id, positions[i + 1]._id)

    r.push({
      originData: {
        lat: parseFloat(positions[i].lat),
        lng: parseFloat(positions[i].lng)
      },
      destinationData: {
        lat: parseFloat(positions[i + 1].lat),
        lng: parseFloat(positions[i + 1].lng)
      },
      data: {
        startLocation: positions[i]._id,
        endLocation: positions[i + 1]._id,
        nodeLocationName: positions[i].nodeLocationName,
        overViewPolyLine
      }
    })
  }

  if (stopAtLocation) {
    const { lat, lng } = stopAtLocation

    r.push({
      originData: {
        lat: parseFloat(positions[positions?.length - 1].lat),
        lng: parseFloat(positions[positions?.length - 1].lng)
      },
      destinationData: {
        lat: parseFloat(lat),
        lng: parseFloat(lng)
      },
      data: {
        startLocation: startAtLocation._id,
        endLocation: positions[0]._id,
        nodeLocationName: positions[positions?.length - 1].nodeLocationName
      }
    })
  }

  const options = {
    multiple: true,
    polylineStyle: $map.polylineStyle.routeDrawStyle
  } as { [key: string]: any }

  routeDraw.value = {
    routes: r,
    options
  }
}

const setDataDrawRouteActual = () => {
  const { details } = CJourneyPositioning.value || {}
  if (!details) {
    return
  }

  const positions = CPositions.value

  const keys = ['start'].concat(positions.map((val: any, index: number) => index))
  const r = [] as any[]

  keys.forEach((key: string | number) => {
    if (details?.[key]?.length && details[key].length >= 2) {
      const locations = details[key]
      const polyline: any[] = []
      locations.forEach((location: any, i: number) => {
        const lat = parseFloat(location.lat?.toString())
        const lng = parseFloat(location.lng?.toString())

        polyline.push({ lat, lng })
      })

      r.push({
        data: {
          overViewPolyLine: polyline
        }
      })
    }
  })

  const options = {
    multiple: true,
    polylineStyle: $map.polylineStyle.routeActual
  } as { [key: string]: any }

  routeActual.value = {
    routes: r,
    options
  }
}

const setDataDrawRouteEstimate = () => {
  const { details, process, currentLocation } = CJourneyPositioning.value || {}
  if (!details) {
    return
  }

  const positions = CPositions.value

  const re: any[] = []

  if (currentLocation.at === 'stage') {
    routeEstimate.value = {
      routes: re
    }
    return
  }

  if (process === 'start') {
    const lat = parseFloat(positions[0].lat?.toString())
    const lng = parseFloat(positions[0].lng?.toString())

    re.push({
      originData: {
        lat: parseFloat(currentLocation.lat),
        lng: parseFloat(currentLocation.lng)
      },
      destinationData: {
        lat,
        lng
      }
    })
  }

  const p = Number(process)

  if (typeof p === 'number' && p < positions.length - 1) {
    const lat = parseFloat(positions[p + 1].lat?.toString())
    const lng = parseFloat(positions[p + 1].lng?.toString())

    re.push({
      originData: {
        lat: parseFloat(currentLocation.lat),
        lng: parseFloat(currentLocation.lng)
      },
      destinationData: {
        lat,
        lng
      }
    })
  }

  if (typeof p === 'number' && p === positions.length - 1) {
    const location = $location.getLocation(journey?.value?.stopAtLocationId)

    const lat = parseFloat(location?.lat?.toString())
    const lng = parseFloat(location?.lng?.toString())
    re.push({
      originData: {
        lat: parseFloat(currentLocation.lat),
        lng: parseFloat(currentLocation.lng)
      },
      destinationData: {
        lat,
        lng
      }
    })
  }

  const options = {
    multiple: true,
    polylineStyle: $map.polylineStyle.routeEstimate,
    startMarker: true
  } as { [key: string]: any }

  routeEstimate.value = {
    routes: re,
    options
  }
}

// set data để render markers điểm trong tuyến
const setDataCreateMarker = () => {
  const positions = CPositions.value
  let originData: google.maps.LatLngLiteral = { lat: 0, lng: 0 }
  let destinationData: google.maps.LatLngLiteral = { lat: 0, lng: 0 }
  let originLocationName = 'A'
  let destinationLocationName = 'B'
  const waypointsData: { location: google.maps.LatLngLiteral; node?: string; locationName?: string }[] = []

  let { startAtLocation, stopAtLocation } = journey?.value || {}
  if (Array.isArray(startAtLocation)) {
    startAtLocation = startAtLocation[0] || null
  }
  if (Array.isArray(stopAtLocation)) {
    stopAtLocation = stopAtLocation[0] || null
  }

  let hasStart = false
  let hasStop = false
  if (startAtLocation) {
    hasStart = true
    const { locationName } = startAtLocation
    let { lat, lng } = startAtLocation
    lat = parseFloat(lat?.toString())
    lng = parseFloat(lng?.toString())
    originData = { lat, lng } as google.maps.LatLngLiteral
    originLocationName = locationName
    latLngLocationRoutes.value.push({ lat, lng, nodeLocationName: locationName } as google.maps.LatLngLiteral)
  }

  if (stopAtLocation) {
    hasStop = true
    const { locationName } = stopAtLocation
    let { lat, lng } = stopAtLocation
    lat = parseFloat(lat?.toString())
    lng = parseFloat(lng?.toString())
    destinationData = { lat, lng } as google.maps.LatLngLiteral
    destinationLocationName = locationName
    latLngLocationRoutes.value.push({ lat, lng, nodeLocationName: locationName } as google.maps.LatLngLiteral)
  }

  positions.forEach((s: any, i: number) => {
    if (isNaN(s.lat) || isNaN(s.lng)) {
      return
    }
    const { details, process, currentLocation } = CJourneyPositioning.value || {}

    const lat = parseFloat(s.lat?.toString())
    const lng = parseFloat(s.lng?.toString())
    let vehicleInStage = false
    if (currentLocation?.at === 'stage' && process !== 'end' && (Object.keys(details)?.length || 0) - 1 === i) {
      vehicleInStage = true
    }

    latLngLocationRoutes.value.push({ lat, lng, locationName: s.locationName } as google.maps.LatLngLiteral)
    const tollStations = getTolltation(positions[i]?._id, positions[i + 1]?._id)

    if (tollStations && tollStations?.length) {
      tollStations.forEach((t: any) => {
        latLngLocationRoutes.value.push({ lat: 0, lng: 0, nodeId: t.locationId, locationName: t.locationName } as google.maps.LatLngLiteral)
      })
    }

    if (i === 0 && !hasStart) {
      originData = { lat, lng, options: { vehicleInStage } } as google.maps.LatLngLiteral
      originLocationName = s.nodeLocationName
      return
    }
    if (i === positions.length - 1 && !hasStop) {
      destinationData = { lat, lng, options: { vehicleInStage } } as google.maps.LatLngLiteral
      destinationLocationName = s.nodeLocationName
      return
    }

    waypointsData.push({
      location: { lat, lng, options: { vehicleInStage } } as google.maps.LatLngLiteral,
      node: s.node,
      locationName: s.nodeLocationName
    })
  })

  route.value = {
    originData,
    destinationData,
    waypointsData,
    options: {
      originLocationName,
      destinationLocationName,
      sameOriginDestination: originData.lat === destinationData.lat && originData.lng === destinationData.lng,
      renderMarkerByIndexNumber: false // journey?.value?.processStatus !== 0
    }
  }
}

const getOverViewPolyLine = (startPositionId: string, endPositionId: string) => {
  let legs = [] as any[]
  const drew: boolean = true
  legs = CRoutingPositions.value?.route?.legs

  if (!legs || !legs.length) {
    return null
  }

  if (!drew) {
    return null
  }

  const leg = legs.filter((item: any) => {
    if (item.startLocation === startPositionId && item.endLocation === endPositionId) {
      return true
    }
    return false
  })

  if (!leg || !leg.length) {
    return []
  }

  return leg[0].overViewPolyLine || ''
}

const getTolltation = (startPositionId: string, endPositionId: string) => {
  let legs = [] as any[]

  legs = routeDraw.value?.route?.legs

  if (!legs || !legs.length) {
    return null
  }

  const leg = legs.filter((item: any) => {
    if (item.startLocation === startPositionId && item.endLocation === endPositionId) {
      return true
    }
    return false
  })

  if (!leg || !leg.length) {
    return []
  }

  return leg[0].tollStations || []
}
</script>
